import { useEffect, useRef } from "react";
import ApplicationCard from "./application_card";
import useTranslation from "@hooks/use_translation";
import MissingApplicationsModal from "@components/applications/missing_applications_modal";
import { Stack } from "@sprout/layout";
import Callout from "@sprout/callout.tsx";
import type Application from "../../types/application";
import LoadingSpinner from "@/sprout/loading_spinner";

type Props = {
  applications: Application[];
  onApplicationSelect: (id: number) => void;
  isLoading?: boolean;
  selectedApplicationId?: number | null | undefined;
  search?: string;
  setSearch?: (search: string) => void;
  showCallout?: boolean;
  narrow?: boolean;
  addLinks?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
};

const ApplicationCardList = function (props: Props) {
  const {
    applications,
    selectedApplicationId,
    onApplicationSelect,
    showCallout = true,
    narrow = false,
    addLinks = false,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  } = props;
  const { t } = useTranslation("applications");
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasNextPage || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  return (
    <Stack gap="8px">
      {applications &&
        applications.map((application) => (
          <ApplicationCard
            key={application.id}
            application={application}
            selected={application.id == selectedApplicationId}
            onApplicationSelect={onApplicationSelect}
            narrow={narrow}
            addLink={addLinks}
          />
        ))}
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isFetchingNextPage && <LoadingSpinner size="small" />}
        </div>
      )}
      {showCallout && !hasNextPage && (
        <div className="application-card application-card__no-formatting">
          <Callout
            headerText={t("not_found_modal.title")}
            link={<MissingApplicationsModal />}
            background={"var(--green-gray)"}
          />
        </div>
      )}
    </Stack>
  );
};

export default ApplicationCardList;
